import { useCallback, useEffect } from "react"

import { useRecoilState } from "recoil"

import { convertApiPluginToInternal } from "@fuse/utils/pluginConverters"

import { pluginStudioAtom } from "../atoms/pluginStudioAtom"
import { fetchPluginList } from "../effects/pluginApiEffects"
import {
  getCodeEditorPluginId,
  Plugin,
  setCodeEditorPluginId,
  toCodeEditorPluginId,
  UserRole,
} from "../types"

/**
 * Hook for loading and managing plugins in the plugin studio
 *
 * This hook is focused on the plugin studio editing experience.
 * For managing plugins in live preview, use the useEventPlugins hook.
 */
export function useStudioPlugins() {
  const [studioState, setStudioState] = useRecoilState(pluginStudioAtom)

  /**
   * Load plugins from the API and merge them into the studio state
   * @returns A promise containing the result of the plugin loading operation
   */
  const loadStudioPlugins = useCallback(async () => {
    try {
      console.log("[useStudioPlugins] Loading plugins from API")
      console.log("[useStudioPlugins] ⚠️ EXPECTING PLUGIN STUDIO INTERFACE ⚠️")
      console.log("[useStudioPlugins] Plugin Studio interface requires:", {
        requiredProperties: [
          "id",
          "name",
          "description",
          "manifest",
          "files",
          "createdAt",
        ],
        optionalProperties: ["code", "lastSaved"],
      })

      // Fetch plugins from API
      const result = await fetchPluginList()

      console.log("[useStudioPlugins] API result:", {
        success: result.success,
        pluginsCount: result.plugins?.length || 0,
        error: result.error || "none",
      })

      if (!result.success) {
        console.error(
          "[useStudioPlugins] Failed to load plugins:",
          result.error,
        )
        return { success: false, error: result.error }
      }

      // Convert API plugins to internal format
      const pluginsToAdd: Plugin[] = []
      console.log(
        `[useStudioPlugins] Processing ${result.plugins.length} plugins for conversion`,
      )

      for (const apiPlugin of result.plugins) {
        try {
          console.log(`[useStudioPlugins] Converting plugin ${apiPlugin.id}`)

          // Log the API plugin structure before conversion
          console.log("[useStudioPlugins] API plugin pre-conversion:", {
            id: apiPlugin.id,
            hasManifest: !!apiPlugin.manifest,
            hasCode: !!apiPlugin.code,
            manifestName: apiPlugin.manifest?.name,
            hasFiles: "files" in apiPlugin,
            codeKeys: apiPlugin.code ? Object.keys(apiPlugin.code) : [],
          })

          // Convert the plugin
          const plugin = convertApiPluginToInternal(apiPlugin)

          // Log the converted plugin to check if it has the required properties
          console.log("[useStudioPlugins] Converted plugin structure check:", {
            id: plugin.id,
            // Check for required Plugin Studio properties
            hasName: "name" in plugin,
            hasDescription: "description" in plugin,
            hasFiles: "files" in plugin,
            hasCreatedAt: "createdAt" in plugin,
            // Additional details
            manifestName: plugin.manifest?.name,
            manifestDesc: plugin.manifest?.description,
            isPluginStudioCompatible:
              "name" in plugin &&
              "description" in plugin &&
              "files" in plugin &&
              "createdAt" in plugin,
          })

          // Warning if plugin doesn't match Plugin Studio interface
          if (!("files" in plugin)) {
            console.error(`[useStudioPlugins] ⚠️ PLUGIN TYPE MISMATCH ⚠️`)
            console.error(
              `[useStudioPlugins] Plugin is missing required 'files' property for Plugin Studio`,
            )
            console.error(
              `[useStudioPlugins] This will cause runtime errors when components try to access plugin.files`,
            )
          }

          // Add to plugins array
          pluginsToAdd.push(plugin)
        } catch (error) {
          console.error(
            `[useStudioPlugins] Error converting plugin ${apiPlugin.id}:`,
            error,
          )
        }
      }

      console.log(
        `[useStudioPlugins] Successfully converted ${pluginsToAdd.length} plugins`,
      )

      // Merge plugins into state
      setStudioState((prevState) => {
        // Create a map of existing plugins for efficient lookup
        const existingPluginsMap = new Map(
          prevState.plugins.map((plugin) => [plugin.id, plugin]),
        )

        // Log the expected structure of plugins in state
        if (prevState.plugins.length > 0) {
          const firstPlugin = prevState.plugins[0]
          console.log("[useStudioPlugins] Expected Plugin Studio structure:", {
            id: firstPlugin.id,
            name: firstPlugin.name,
            description: firstPlugin.description,
            hasManifest: !!firstPlugin.manifest,
            hasFiles: Array.isArray(firstPlugin.files),
            filesCount: firstPlugin.files?.length || 0,
            hasCreatedAt: !!firstPlugin.createdAt,
          })
        }

        // Add each new plugin, preserving existing ones
        const mergedPlugins = [...prevState.plugins]

        for (const plugin of pluginsToAdd) {
          // Skip if this is a local-only plugin that already exists
          if (existingPluginsMap.has(plugin.id)) {
            continue
          }

          // Log the structure of the plugin being added to state
          console.log(
            `[useStudioPlugins] Adding plugin ${plugin.id} to state:`,
            {
              hasName: "name" in plugin,
              hasDescription: "description" in plugin,
              hasFiles: "files" in plugin,
              hasCreatedAt: "createdAt" in plugin,
              isPluginStudioCompatible:
                "name" in plugin &&
                "description" in plugin &&
                "files" in plugin &&
                "createdAt" in plugin,
            },
          )

          // Add the new plugin
          mergedPlugins.push(plugin)
        }

        console.log(
          `[useStudioPlugins] Updated studio state with ${mergedPlugins.length} total plugins`,
        )

        return {
          ...prevState,
          plugins: mergedPlugins,
        }
      })

      console.log(
        `[useStudioPlugins] Successfully loaded ${pluginsToAdd.length} plugins`,
      )
      return { success: true, data: pluginsToAdd }
    } catch (error) {
      console.error("[useStudioPlugins] Error loading plugins:", error)
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }, [setStudioState])

  /**
   * Select a plugin as active in the studio
   * @param pluginId The ID of the plugin to set as active
   */
  const selectPlugin = useCallback(
    (pluginId: string) => {
      setStudioState((prevState) => {
        // Find the plugin
        const plugin = prevState.plugins.find((p) => p.id === pluginId)
        if (!plugin) {
          console.warn(`[useStudioPlugins] Plugin ${pluginId} not found`)
          return prevState
        }

        // Log the structure of the selected plugin
        console.log(`[useStudioPlugins] Selecting plugin ${pluginId}:`, {
          hasName: "name" in plugin,
          hasDescription: "description" in plugin,
          hasFiles: "files" in plugin,
          hasCreatedAt: "createdAt" in plugin,
          isPluginStudioCompatible:
            "name" in plugin &&
            "description" in plugin &&
            "files" in plugin &&
            "createdAt" in plugin,
        })

        // Initialize plugin state if it doesn't exist
        const pluginState = prevState.pluginStates[pluginId] || {
          activeFileId: null,
          openFileIds: [],
          activeViewType: UserRole.GUEST,
        }

        // Use the setter function to update the codeEditorPluginId
        const updatedState = setCodeEditorPluginId(prevState, pluginId)

        return {
          ...updatedState,
          pluginStates: {
            ...prevState.pluginStates,
            [pluginId]: pluginState,
          },
        }
      })
    },
    [setStudioState],
  )

  /**
   * Add a new plugin to the studio
   * @param plugin The plugin to add
   */
  const addPlugin = useCallback(
    (plugin: Plugin) => {
      console.log(`[useStudioPlugins] Adding new plugin: ${plugin.name}`)

      setStudioState((prevState) => {
        // Check if plugin already exists
        const existingPlugin = prevState.plugins.find((p) => p.id === plugin.id)
        if (existingPlugin) {
          console.warn(
            `[useStudioPlugins] Plugin ${plugin.id} already exists, replacing it`,
          )
        }

        // Add or replace the plugin
        const updatedPlugins = existingPlugin
          ? prevState.plugins.map((p) => (p.id === plugin.id ? plugin : p))
          : [...prevState.plugins, plugin]

        // Initialize plugin state
        const pluginState = {
          activeFileId:
            plugin.files.find((f) => f.type === UserRole.GUEST)?.id || null,
          openFileIds: [
            plugin.files.find((f) => f.type === UserRole.GUEST)?.id || "",
          ],
          activeViewType: UserRole.GUEST,
        }

        return {
          ...prevState,
          plugins: updatedPlugins,
          pluginStates: {
            ...prevState.pluginStates,
            [plugin.id]: pluginState,
          },
        }
      })
    },
    [setStudioState],
  )

  // Additional functionality can be added here as needed

  return {
    plugins: studioState.plugins,
    activePluginId: getCodeEditorPluginId(studioState), // Use the accessor function
    loadStudioPlugins,
    selectPlugin,
    addPlugin,
  }
}
